import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { es } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Button } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

type CalendarView = "days" | "months" | "years";

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  // `defaultMonth`: arranca en ese mes sin controlarlo (p. ej. el de la fecha ya elegida).
  const [month, setMonth] = React.useState<Date>(props.month || props.defaultMonth || new Date());
  const [view, setView] = React.useState<CalendarView>("days");
  const [yearRangeStart, setYearRangeStart] = React.useState<number>(
    Math.floor(new Date().getFullYear() / 10) * 10
  );

  const handleMonthChange = (newMonth: Date) => {
    setMonth(newMonth);
    props.onMonthChange?.(newMonth);
  };

  // Extract onSelect to wrap it for auto-navigating to selected date's month.
  // Soporta los 3 modos de react-day-picker:
  //   - single   → onSelect(day: Date | undefined, ...)
  //   - range    → onSelect(range: DateRange { from?, to? } | undefined, ...)
  //   - multiple → onSelect(days: Date[] | undefined, ...)
  // Sin esta diferenciación, en modo range el primer argumento es un objeto
  // {from, to} y llamar .getMonth() sobre él rompe la selección.
  const originalOnSelect = (props as any).onSelect;
  const wrappedOnSelect = React.useCallback((...args: any[]) => {
    const first = args[0];
    let dateForNav: Date | undefined;
    if (first instanceof Date) {
      dateForNav = first;
    } else if (Array.isArray(first) && first.length > 0) {
      dateForNav = first[first.length - 1];
    } else if (first && typeof first === "object") {
      // DateRange: navegar al mes del extremo más reciente seleccionado.
      const r = first as { from?: Date; to?: Date };
      dateForNav = r.to ?? r.from;
    }
    if (
      dateForNav &&
      (dateForNav.getMonth() !== month.getMonth() ||
        dateForNav.getFullYear() !== month.getFullYear())
    ) {
      handleMonthChange(new Date(dateForNav.getFullYear(), dateForNav.getMonth(), 1));
    }
    if (originalOnSelect) {
      originalOnSelect(...args);
    }
  }, [month, originalOnSelect]);

  const months = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const currentYear = month.getFullYear();
  const currentMonth = month.getMonth();

  // Vista de años
  if (view === "years") {
    const years = Array.from({ length: 12 }, (_, i) => yearRangeStart + i);
    
    return (
      <div className={cn("p-3 pointer-events-auto", className)}>
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => setYearRangeStart(yearRangeStart - 10)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium">
            {yearRangeStart} - {yearRangeStart + 11}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => setYearRangeStart(yearRangeStart + 10)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {years.map((year) => (
            <Button
              key={year}
              variant={year === currentYear ? "default" : "ghost"}
              className="h-14 text-sm"
              onClick={() => {
                const newDate = new Date(month);
                newDate.setFullYear(year);
                handleMonthChange(newDate);
                setView("months");
              }}
            >
              {year}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  // Vista de meses
  if (view === "months") {
    return (
      <div className={cn("p-3 pointer-events-auto", className)}>
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              const newDate = new Date(month);
              newDate.setFullYear(currentYear - 1);
              handleMonthChange(newDate);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            className="text-sm font-medium hover:bg-accent"
            onClick={() => setView("years")}
          >
            {currentYear}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              const newDate = new Date(month);
              newDate.setFullYear(currentYear + 1);
              handleMonthChange(newDate);
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {months.map((monthName, index) => (
            <Button
              key={index}
              variant={index === currentMonth ? "default" : "ghost"}
              className="h-14 text-sm"
              onClick={() => {
                const newDate = new Date(month);
                newDate.setMonth(index);
                handleMonthChange(newDate);
                setView("days");
              }}
            >
              {monthName}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  // Vista de días (DayPicker normal)
    const { onSelect: _onSelect, ...restProps } = props as any;
    return (
    <DayPicker
      locale={es}
      showOutsideDays={showOutsideDays}
      className={cn("p-3 pointer-events-auto", className)}
      month={month}
      onMonthChange={handleMonthChange}
      onSelect={wrappedOnSelect}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside aria-selected:bg-primary/20 aria-selected:text-primary aria-selected:opacity-100",
        day_disabled: "text-muted-foreground opacity-30 cursor-not-allowed",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
        Caption: ({ displayMonth }) => {
          return (
            <div className="flex justify-center pt-1 relative items-center">
              <button
                type="button"
                onClick={() => {
                  const newDate = new Date(month);
                  newDate.setMonth(newDate.getMonth() - 1);
                  handleMonthChange(newDate);
                }}
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1",
                )}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <Button
                variant="ghost"
                className="text-sm font-medium hover:bg-accent"
                onClick={() => setView("months")}
              >
                {months[displayMonth.getMonth()]} {displayMonth.getFullYear()}
              </Button>
              <button
                type="button"
                onClick={() => {
                  const newDate = new Date(month);
                  newDate.setMonth(newDate.getMonth() + 1);
                  handleMonthChange(newDate);
                }}
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1",
                )}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          );
        },
      }}
      {...restProps}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
